/**
 * ScheduleModal — put a saved search on a daily/weekly/monthly schedule at a
 * fixed tenant-tz hour, and pick who gets the post-run email (CRM team
 * multi-select, "new matches only" vs "every run"). Wires the EXISTING
 * backend fields — PATCH /searches/:id { schedule, schedule_enabled, notify }
 * recomputes next_run_at server-side; the worker tick claims + executes + and
 * emails (docs/crm-search-builder-plan.md §6.5; O-5 email-only).
 */
import { useEffect, useState } from 'react';
import { CalendarClock, Loader2 } from 'lucide-react';
import api from '../../../services/api';
import type { SavedSearch, TeamMember } from '../../types';
import { FilterMultiSelect } from '../filters';
import { relTime } from '../../lib';
import { Alert, Z } from '../ui';

interface Props {
  search: Pick<SavedSearch, 'id' | 'name' | 'schedule' | 'schedule_enabled' | 'notify'>;
  team: TeamMember[];
  onSaved: (updated: SavedSearch) => void;
  onClose: () => void;
}

/** The server's plain sentence (`error`) — never its raw `detail`. */
const errMsg = (e: any): string =>
  e?.response?.data?.error
  || (e?.response ? "Couldn't save the schedule. Please try again." : "Couldn't reach the server. Check your connection and try again.");

const DOW_OPTIONS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const HOURS = Array.from({ length: 24 }, (_, h) => ({
  value: h,
  label: `${((h + 11) % 12) + 1}:00 ${h < 12 ? 'AM' : 'PM'}`,
}));

const fieldCls = 'mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100';
const labelCls = 'mt-3 block text-xs font-medium text-gray-600 dark:text-gray-300';

export default function ScheduleModal({ search, team, onSaved, onClose }: Props) {
  const [enabled, setEnabled] = useState(search.schedule_enabled);
  const [freq, setFreq] = useState<string>(search.schedule?.freq || 'daily');
  const [hour, setHour] = useState<number>(search.schedule?.hour ?? 7);
  const [dow, setDow] = useState<number>(search.schedule?.dow ?? 1);
  const [dom, setDom] = useState<number>(search.schedule?.dom ?? 1);
  const [notifyIds, setNotifyIds] = useState<string[]>(search.notify?.user_ids || []);
  const [notifyOn, setNotifyOn] = useState<string>(search.notify?.on || 'new_matches');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const schedule = enabled
        ? { freq, hour, ...(freq === 'weekly' ? { dow } : {}), ...(freq === 'monthly' ? { dom } : {}) }
        : (search.schedule || null); // keep the stored cadence when just pausing
      const res = await api.patch(`/api/crm/searches/${search.id}`, {
        schedule,
        schedule_enabled: enabled,
        notify: notifyIds.length ? { user_ids: notifyIds, on: notifyOn } : null,
      });
      onSaved(res.data?.search);
      onClose();
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`fixed inset-0 ${Z.modal} flex items-start justify-center overflow-y-auto bg-black/40 p-4`} onClick={onClose} role="presentation">
      <div
        className="my-auto w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Schedule ${search.name}`}
      >
        <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <CalendarClock className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          Schedule “{search.name}”
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Re-run automatically and email the team when the list changes. Scheduled runs never
          touch leads — a human reviews the run first.
        </p>

        <label className="mt-4 flex items-center gap-2 text-sm text-gray-800 dark:text-gray-100">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          Run on a schedule
        </label>

        {enabled && (
          <>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">Frequency</label>
                <select value={freq} onChange={(e) => setFreq(e.target.value)} aria-label="Frequency" className={fieldCls}>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">
                  At hour <span className="font-normal text-gray-400">(Eastern time)</span>
                </label>
                <select value={hour} onChange={(e) => setHour(Number(e.target.value))} aria-label="Hour" className={fieldCls}>
                  {HOURS.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
                </select>
              </div>
            </div>
            {freq === 'weekly' && (
              <div className="mt-2">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">On</label>
                <select value={dow} onChange={(e) => setDow(Number(e.target.value))} aria-label="Day of week" className={fieldCls}>
                  {DOW_OPTIONS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                </select>
              </div>
            )}
            {freq === 'monthly' && (
              <div className="mt-2">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">
                  On day <span className="font-normal text-gray-400">(1–28, so every month has it)</span>
                </label>
                <select value={dom} onChange={(e) => setDom(Number(e.target.value))} aria-label="Day of month" className={fieldCls}>
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            )}
          </>
        )}

        <label className={labelCls}>Email after a scheduled run</label>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <FilterMultiSelect
            values={notifyIds}
            onChange={setNotifyIds}
            options={[{ value: '', label: 'Nobody' }, ...team.map((m) => ({ value: m.id, label: m.name }))]}
            label="Recipients"
          />
          {notifyIds.length > 0 && (
            <select value={notifyOn} onChange={(e) => setNotifyOn(e.target.value)} aria-label="Notify when" className="rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900 focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100">
              <option value="new_matches">Only when there are new matches</option>
              <option value="always">After every run</option>
            </select>
          )}
        </div>
        {notifyIds.length > 0 && !enabled && (
          <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-400">
            Recipients are saved, but emails only go out after scheduled runs — enable the schedule above.
          </p>
        )}

        {error && (
          <Alert tone="error" size="sm" icon={false} className="mt-3">{error}</Alert>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700">
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save schedule
          </button>
        </div>
      </div>
    </div>
  );
}

/** "Daily 7 AM ET" chip text from stored schedule fields (list rows, builder). */
export function scheduleChipLabel(s: Pick<SavedSearch, 'schedule' | 'schedule_enabled' | 'next_run_at'>): string {
  if (!s.schedule_enabled || !s.schedule) return 'Manual';
  const h = s.schedule.hour ?? 0;
  const hh = `${((h + 11) % 12) + 1}${h < 12 ? 'am' : 'pm'}`;
  const base = s.schedule.freq === 'daily' ? `Daily ${hh}`
    : s.schedule.freq === 'weekly' ? `${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][s.schedule.dow ?? 0]} ${hh}`
      : `Monthly d${s.schedule.dom ?? 1} ${hh}`;
  return s.next_run_at ? `${base} · next ${relTime(s.next_run_at)}` : base;
}
